import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useLeague, useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { League, MeLeagueStats } from '@shootout-comps/types';
import { useRequiredParams } from '../../hooks/useRequiredParams';
import LeaguesListPage from './LeaguesListPage';
import LeagueDashboardPage from './LeagueDashboardPage';
import DivisionTeamsPage from './DivisionTeamsPage';
import DivisionFixturesPage from './DivisionFixturesPage';
import DivisionFixturesByDatePage from './DivisionFixturesByDatePage';
import DivisionStandingsPage from './DivisionStandingsPage';
import FixtureDetailPage from './FixtureDetailPage';
import FixtureFramePage from './FixtureFramePage';
import PublicLeaguesPage from './PublicLeaguesPage';
import PublicLeagueDetailPage from './PublicLeagueDetailPage';
import SpectateLeagueMatchPage from './SpectateLeagueMatchPage';
import SeasonHistoryPage from './SeasonHistoryPage';
import TeamRosterPage from './TeamRosterPage';
import PlayerHistoryPage from './PlayerHistoryPage';

type DivisionTab = 'teams' | 'fixtures' | 'standings' | 'history';

function divisionTabUrl(leagueId: string, divisionId: string, tab: DivisionTab) {
  return `/leagues/${leagueId}/divisions/${divisionId}/${tab}`;
}

// ─── Route adapters ─────────────────────────────────────────────────────────
// Mirrors App.tsx's adapter pattern: leaf pages keep their existing prop contracts, only these
// small wrappers translate URL params <-> props. Mounted at `/leagues/*` (see App.tsx), so every
// path below is relative to that — but navigate() targets are written as absolute paths (starting
// `/leagues/...`) throughout, to sidestep react-router's relative-path resolution subtleties.

type LinkedLeague = MeLeagueStats['leagues'][number];

// A linked player can appear more than once (multiple divisions/teams in the same league) —
// collapse to one row per league for this "leagues you play in" list.
function dedupeLinkedLeagues(entries: LinkedLeague[]): LinkedLeague[] {
  const seen = new Set<string>();
  return entries.filter(e => (seen.has(e.leagueId) ? false : (seen.add(e.leagueId), true)));
}

// Entry point for a signed-in user who has been linked to a roster player (self-claimed, or
// linked by an owner/admin) but doesn't own/administer the league themself — they get full
// read-only access to it (all divisions, standings, fixtures, teams) via the same
// /leagues/public/:leagueId route used for browsing public leagues, whether or not the league is
// actually marked public (see publicLeague.routes.ts's readableLeagueFilter on the API side).
function LinkedLeaguesSection({ linkedLeagues }: { linkedLeagues: LinkedLeague[] }) {
  const navigate = useNavigate();
  if (linkedLeagues.length === 0) return null;
  return (
    <div style={{ maxWidth: '480px', margin: '0 auto 1.75rem', textAlign: 'left' }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
        Leagues you play in
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {dedupeLinkedLeagues(linkedLeagues).map(l => (
          <div
            key={l.leagueId}
            onClick={() => navigate(`/leagues/public/${l.leagueId}`)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem', cursor: 'pointer' }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>🏆 {l.leagueName}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.1rem' }}>{l.divisionName} · {l.teamName}</div>
            </div>
            <span style={{ color: 'var(--chalk-dim)' }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeaguesIndexRoute({ leagues, linkedLeagues, createForm }: { leagues: League[]; linkedLeagues: LinkedLeague[]; createForm: React.ReactNode }) {
  const { t } = useLang();
  const navigate = useNavigate();

  if (leagues.length === 0) {
    return (
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
        <LinkedLeaguesSection linkedLeagues={linkedLeagues} />
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏆</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.5rem)', color: 'var(--gold)', marginBottom: '0.75rem' }}>{t.navLeagues}</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.75rem' }}>
          {t.leaguesEmptyDesc}
        </p>
        {createForm}
        <button onClick={() => navigate('/leagues/public')} style={{ marginTop: '1.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: 'var(--chalk-dim)', padding: '0.5rem 1rem', fontSize: '0.8rem', cursor: 'pointer' }}>
          🌐 Browse Public Leagues
        </button>
      </div>
    );
  }
  if (leagues.length === 1) {
    return <Navigate to={`/leagues/${leagues[0]._id}`} replace />;
  }
  return (
    <>
      <LinkedLeaguesSection linkedLeagues={linkedLeagues} />
      <LeaguesListPage
        leagues={leagues}
        onOpen={id => navigate(`/leagues/${id}`)}
        createForm={createForm}
        onBrowsePublic={() => navigate('/leagues/public')}
      />
    </>
  );
}

function LeagueDashboardRoute({ leaguesCount, reload }: { leaguesCount: number; reload: () => void }) {
  const params = useRequiredParams<{ leagueId: string }>(['leagueId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId } = params;
  return (
    <LeagueDashboardPage
      leagueId={leagueId}
      onBack={leaguesCount > 1 ? () => navigate('/leagues') : undefined}
      onOpenDivision={divId => navigate(divisionTabUrl(leagueId, divId, 'teams'))}
      onDeleted={() => { reload(); navigate('/leagues'); }}
      onChanged={reload}
      onBrowsePublic={() => navigate('/leagues/public')}
    />
  );
}

function DivisionTeamsRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string }>(['leagueId', 'divisionId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId } = params;
  return (
    <DivisionTeamsPage
      leagueId={leagueId}
      divisionId={divisionId}
      onBack={() => navigate(`/leagues/${leagueId}`)}
      onSwitchToFixtures={() => navigate(divisionTabUrl(leagueId, divisionId, 'fixtures'))}
      onSwitchToStandings={() => navigate(divisionTabUrl(leagueId, divisionId, 'standings'))}
      onSwitchToHistory={() => navigate(divisionTabUrl(leagueId, divisionId, 'history'))}
      onOpenTeam={teamId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/teams/${teamId}`)}
    />
  );
}

function DivisionFixturesRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string }>(['leagueId', 'divisionId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId } = params;
  return (
    <DivisionFixturesPage
      leagueId={leagueId}
      divisionId={divisionId}
      onBack={() => navigate(`/leagues/${leagueId}`)}
      onSwitchToTeams={() => navigate(divisionTabUrl(leagueId, divisionId, 'teams'))}
      onSwitchToStandings={() => navigate(divisionTabUrl(leagueId, divisionId, 'standings'))}
      onSwitchToHistory={() => navigate(divisionTabUrl(leagueId, divisionId, 'history'))}
      onOpenFixture={fixtureId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}`)}
      onOpenDateFixtures={dateParam => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/date/${dateParam}`)}
    />
  );
}

function FixturesByDateRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string; date: string }>(['leagueId', 'divisionId', 'date'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId, date } = params;
  return (
    <DivisionFixturesByDatePage
      leagueId={leagueId}
      divisionId={divisionId}
      date={date}
      onBack={() => navigate(divisionTabUrl(leagueId, divisionId, 'fixtures'))}
      onOpenFixture={fixtureId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}`)}
    />
  );
}

function DivisionStandingsRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string }>(['leagueId', 'divisionId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId } = params;
  return (
    <DivisionStandingsPage
      leagueId={leagueId}
      divisionId={divisionId}
      onBack={() => navigate(`/leagues/${leagueId}`)}
      onSwitchToTeams={() => navigate(divisionTabUrl(leagueId, divisionId, 'teams'))}
      onSwitchToFixtures={() => navigate(divisionTabUrl(leagueId, divisionId, 'fixtures'))}
      onSwitchToHistory={() => navigate(divisionTabUrl(leagueId, divisionId, 'history'))}
      onOpenTeam={teamId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/teams/${teamId}`)}
    />
  );
}

function SeasonHistoryRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string }>(['leagueId', 'divisionId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId } = params;
  return (
    <SeasonHistoryPage
      leagueId={leagueId}
      divisionId={divisionId}
      onBack={() => navigate(`/leagues/${leagueId}`)}
      onSwitchToTeams={() => navigate(divisionTabUrl(leagueId, divisionId, 'teams'))}
      onSwitchToFixtures={() => navigate(divisionTabUrl(leagueId, divisionId, 'fixtures'))}
      onSwitchToStandings={() => navigate(divisionTabUrl(leagueId, divisionId, 'standings'))}
    />
  );
}

function FixtureDetailRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string; fixtureId: string }>(['leagueId', 'divisionId', 'fixtureId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId, fixtureId } = params;
  return (
    <FixtureDetailPage
      leagueId={leagueId}
      divisionId={divisionId}
      fixtureId={fixtureId}
      onBack={() => navigate(divisionTabUrl(leagueId, divisionId, 'fixtures'))}
      onOpenFrame={matchId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}/frame/${matchId}`)}
    />
  );
}

function FixtureFrameRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string; fixtureId: string; matchId: string }>(
    ['leagueId', 'divisionId', 'fixtureId', 'matchId'], '/leagues'
  );
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId, fixtureId, matchId } = params;
  return (
    <FixtureFramePage
      matchId={matchId}
      onBack={() => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}`)}
    />
  );
}

function TeamRosterRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string; teamId: string }>(['leagueId', 'divisionId', 'teamId'], '/leagues');
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId, teamId } = params;
  return (
    <TeamRosterPage
      leagueId={leagueId}
      divisionId={divisionId}
      teamId={teamId}
      onBack={() => navigate(divisionTabUrl(leagueId, divisionId, 'standings'))}
      onOpenPlayer={playerId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/teams/${teamId}/players/${playerId}`)}
      onOpenFixture={fixtureId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}`)}
    />
  );
}

function PlayerHistoryRoute() {
  const params = useRequiredParams<{ leagueId: string; divisionId: string; teamId: string; playerId: string }>(
    ['leagueId', 'divisionId', 'teamId', 'playerId'], '/leagues'
  );
  const navigate = useNavigate();
  if (!params) return null;
  const { leagueId, divisionId, teamId } = params;
  return (
    <PlayerHistoryPage
      leagueId={leagueId}
      divisionId={divisionId}
      teamId={teamId}
      playerId={params.playerId}
      onBack={() => navigate(`/leagues/${leagueId}/divisions/${divisionId}/teams/${teamId}`)}
      onSelectFixture={fixtureId => navigate(`/leagues/${leagueId}/divisions/${divisionId}/fixtures/${fixtureId}`)}
    />
  );
}

function PublicLeaguesRoute() {
  const navigate = useNavigate();
  return (
    <PublicLeaguesPage
      onSelectLeague={id => navigate(`/leagues/public/${id}`)}
      onBack={() => navigate('/leagues')}
    />
  );
}

function PublicLeagueDetailRoute() {
  const params = useRequiredParams<{ leagueId: string }>(['leagueId'], '/leagues/public');
  const navigate = useNavigate();
  if (!params) return null;
  return (
    <PublicLeagueDetailPage
      leagueId={params.leagueId}
      onBack={() => navigate('/leagues/public')}
      onSpectateMatch={(leagueId, matchId) => navigate(`/leagues/public/${leagueId}/spectate/${matchId}`)}
    />
  );
}

function SpectateLeagueMatchRoute() {
  const params = useRequiredParams<{ leagueId: string; matchId: string }>(['leagueId', 'matchId'], '/leagues/public');
  const navigate = useNavigate();
  if (!params) return null;
  return (
    <SpectateLeagueMatchPage
      leagueId={params.leagueId}
      matchId={params.matchId}
      onBack={() => navigate(`/leagues/public/${params.leagueId}`)}
    />
  );
}

// ─── Top level ──────────────────────────────────────────────────────────────
// Owns the leagues list (needed by several routes below: the index route's list-vs-empty-state
// choice, and the dashboard route's "only show a back button if there's more than one league"
// check) and the create-league form. All league sub-navigation is real URL routing, nested under
// `/leagues/*` (mounted once in App.tsx) — self-contained to this directory, matching this
// feature area's existing "keep it separate" design.
export default function LeaguesLandingPage() {
  const { fetchLeagues, createLeague } = useLeague();
  const { fetchLeagueStats } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<League[] | null>(null);
  const [linkedLeagues, setLinkedLeagues] = useState<LinkedLeague[]>([]);
  const [loadError, setLoadError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newName, setNewName] = useState('');

  const reload = useCallback(() => {
    fetchLeagues()
      .then(setLeagues)
      .catch(e => setLoadError(e instanceof Error ? e.message : 'Failed to load leagues'));
    // Leagues this account is linked to as a roster player (self-claimed, or linked by an
    // owner/admin) — surfaced separately since fetchLeagues() only returns leagues owned/
    // administered by this user, not ones they're merely a participant in.
    fetchLeagueStats().then(s => setLinkedLeagues(s.leagues)).catch(() => setLinkedLeagues([]));
  }, [fetchLeagues, fetchLeagueStats]);

  useEffect(reload, [reload]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true); setCreateError('');
    try {
      const created = await createLeague(newName.trim());
      setNewName('');
      setLeagues(prev => [created, ...(prev ?? [])]);
      navigate(`/leagues/${created._id}`);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create league');
    } finally {
      setCreating(false);
    }
  };

  if (loadError) {
    return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{loadError}</div>;
  }
  if (leagues === null) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;
  }

  const createForm = (
    <div style={{ maxWidth: '420px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }}
          placeholder={t.leagueNamePlaceholder}
          style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 0.85rem', color: 'var(--chalk)', fontSize: '0.9rem', outline: 'none' }}
        />
        <button
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.65rem 1.1rem', fontSize: '0.85rem', fontWeight: 700, cursor: creating || !newName.trim() ? 'not-allowed' : 'pointer', opacity: creating || !newName.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}
        >
          {creating ? '…' : t.createLeagueBtn}
        </button>
      </div>
      {createError && <p style={{ color: '#e74c3c', fontSize: '0.8rem', margin: 0 }}>{createError}</p>}
    </div>
  );

  return (
    <Routes>
      <Route index element={<LeaguesIndexRoute leagues={leagues} linkedLeagues={linkedLeagues} createForm={createForm} />} />
      <Route path="public" element={<PublicLeaguesRoute />} />
      <Route path="public/:leagueId" element={<PublicLeagueDetailRoute />} />
      <Route path="public/:leagueId/spectate/:matchId" element={<SpectateLeagueMatchRoute />} />
      <Route path=":leagueId" element={<LeagueDashboardRoute leaguesCount={leagues.length} reload={reload} />} />
      <Route path=":leagueId/divisions/:divisionId/teams" element={<DivisionTeamsRoute />} />
      <Route path=":leagueId/divisions/:divisionId/fixtures" element={<DivisionFixturesRoute />} />
      <Route path=":leagueId/divisions/:divisionId/fixtures/date/:date" element={<FixturesByDateRoute />} />
      <Route path=":leagueId/divisions/:divisionId/standings" element={<DivisionStandingsRoute />} />
      <Route path=":leagueId/divisions/:divisionId/history" element={<SeasonHistoryRoute />} />
      <Route path=":leagueId/divisions/:divisionId/fixtures/:fixtureId" element={<FixtureDetailRoute />} />
      <Route path=":leagueId/divisions/:divisionId/fixtures/:fixtureId/frame/:matchId" element={<FixtureFrameRoute />} />
      <Route path=":leagueId/divisions/:divisionId/teams/:teamId" element={<TeamRosterRoute />} />
      <Route path=":leagueId/divisions/:divisionId/teams/:teamId/players/:playerId" element={<PlayerHistoryRoute />} />
      <Route path="*" element={<Navigate to="/leagues" replace />} />
    </Routes>
  );
}
